import {
  SERVICE_3D_TRACKING,
  tracking_subservices,
} from "@/components/common/ServiceDots";
import { TOKEN_KEY } from "@/env";

import { colorVariables } from "@/theme/colors";
import { captureException } from "@sentry/react";
import { toast } from "sonner";

export function assertUnreachable(): null {
  try {
    throw new Error("Didn't expect to get here");
  } catch (e) {
    captureException(e);
  }
  return null;
}

export interface Edge<T> {
  node: T;
}

export interface Connection<T> {
  edges: (Edge<T> | null)[];
}

export function getArrayFilterFromParams<T = string>(
  value: string | null,
): T[] {
  if (!value) return [];

  return value.split(",") as unknown as T[];
}

export function toTitleCase(text: string, locale = navigator.language) {
  return text
    .toLocaleLowerCase(locale)
    .replace(/(?<!\S)\S/gu, (match) => match.toLocaleUpperCase(locale));
}

export function mapNodes<Node>(
  connection: Connection<Node> | null | undefined,
): Node[];

export function mapNodes<Result, Node>(
  connection: Connection<Node> | null | undefined,
  callback: (node: Node) => Result,
): Result[];

export function mapNodes<Node, Result = Node>(
  connection: Connection<Node> | null | undefined,
  callback?: (item: Node) => Result,
) {
  const edges = (connection?.edges || []).filter(
    (edge): edge is Edge<Node> => !!edge,
  );

  if (callback) {
    return edges.map(({ node }) => callback(node));
  }

  return edges.map(({ node }) => node);
}

export function longTextFormatter(
  text: string | null | undefined,
  maxLength: number,
) {
  if (!text) {
    return "";
  }
  if (text.length > maxLength) {
    return text.slice(0, maxLength) + "...";
  }
  return text;
}

export const isGlobalId = (str: string) => {
  try {
    const decoded = atob(str);
    return decoded.split(":").length === 2;
  } catch {
    return false;
  }
};

export const formatShotWithSubServiceName = (
  taskShotName: string,
  subService?: { id: string; name: string } | null,
) => {
  if (subService) {
    if (tracking_subservices.includes(subService.name)) {
      return taskShotName;
    }
    return `${taskShotName} -- ${subService.name}`;
  }
  return taskShotName;
};

export const getServiceNameBasedOnSubservice = (
  serviceName: string,
  subService?: { id: string; name: string } | null,
  includeSubService = false,
) => {
  if (subService && serviceName === SERVICE_3D_TRACKING) {
    return includeSubService
      ? `${serviceName} -- ${subService.name}`
      : subService.name;
  }

  return serviceName;
};

export function getCSSVariable(varName: string) {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
}

export function getServiceColor(serviceName: string) {
  const parts = serviceName.toLowerCase().split(" ");
  const formattedName = parts
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");
  return getCSSVariable(
    colorVariables[`service${formattedName}` as keyof typeof colorVariables],
  );
}

export function getPercentage(
  value: number,
  total: number,
  decimalPlaces = 2,
): string {
  if (total === 0) {
    return "0.00%";
  }
  const percentage = (value / total) * 100;
  return `${percentage.toFixed(decimalPlaces)}%`;
}

export const cleanEmails = (emails: string) => {
  const emailsList = emails
    .split(",")
    .map((email) => email.trim().toLowerCase());
  if (emailsList[emailsList.length - 1] === "") emailsList.pop();
  const emailsSet = new Set(emailsList);
  const emailsArray = Array.from(emailsSet);
  return emailsArray;
};

export const areEmailsValid = (emails: string[]) => {
  const isValid = emails.map((email) =>
    String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      ),
  );
  return isValid.every((a) => !!a === true);
};

export const getSingularOrPlural = (count: number, singular: string) => {
  return count === 1 ? singular : `${singular}s`;
};

export function formatCurrency(
  inputOrAmount: string | number | null,
  currencyCode?: string,
): string {
  let amount: number;
  let currency: string;

  if (!inputOrAmount) return "";

  if (typeof inputOrAmount === "number") {
    amount = inputOrAmount;
    if (!currencyCode) {
      throw new Error("Currency code is required when passing a number.");
    }
    currency = currencyCode.toUpperCase();
  } else {
    const parts = inputOrAmount.trim().split(/\s+/);
    if (parts.length !== 2) {
      throw new Error(
        'Invalid input format. Use "<amount> <currencyCode>" or (amount, currencyCode).',
      );
    }
    amount = parseFloat(parts[0]);
    if (isNaN(amount)) {
      throw new Error("Invalid amount in input string.");
    }
    currency = parts[1].toUpperCase();
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

export const hexToRGBA = (hex: string, opacity: number) => {
  hex = hex.replace("#", "");

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export const downloadFileWithAuth = async (
  url: string,
  fileName: string,
): Promise<void> => {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) throw new Error("No auth token");

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const ct = response.headers.get("content-type");
      if (ct?.includes("text/html")) {
        const html = await response.text();
        const blob = new Blob([html], { type: "text/html" });
        const tabURL = URL.createObjectURL(blob);
        window.open(tabURL, "_blank");
        return;
      }
      throw new Error(`Download failed (${response.status})`);
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    const a = document.createElement("a");
    document.body.appendChild(a);
    a.href = blobUrl;
    a.download = fileName;
    a.style.display = "none";
    a.click();
    a.remove();

    setTimeout(() => {
      URL.revokeObjectURL(blobUrl);
    }, 100);
  } catch (err) {
    toast.error(`Download failed: ${(err as Error).message}`);
  }
};
